import { assign, createMachine, MachineConfig, MachineOptions } from "xstate";
import { SegmentBulkMutationData, SegmentBulkUpdateResponse } from "src/hooks/segments/useSegmentBulkUpdateMutation";
import { APIError, NetworkUpdateStatus, Scenario } from "src/types";

interface Context {
  scenario: Scenario | null;
  formData: SegmentBulkMutationData;
  bulkUpdateResponse: SegmentBulkUpdateResponse;
  error: APIError[];
}

type SubmitFormEvent = { type: "SUBMIT_FORM"; payload: SegmentBulkMutationData };
type CancelEvent = { type: "CANCEL" };
type PreviousEvent = { type: "PREVIOUS" };

// Thank you to this article for TS help, not perfect but less tears
//   https://dev.to/giantmachines/an-introduction-to-xstate-in-typescript-1pdn
const xStateConfig: MachineConfig<Context, any, any> = {
  id: "bulkEdit",
  initial: "initialStatusCheck",
  states: {
    initialStatusCheck: {
      initial: "fetching",
      states: {
        fetching: {
          invoke: {
            src: "fetchUpdateStatus",
            onDone: [
              {
                actions: assign({ scenario: (context, event) => event.data }),
                cond: "updateFinished",
                target: "finished",
              },
              {
                target: "retry",
              },
            ],
          },
        },
        finished: {
          type: "final",
        },
        retry: {
          after: {
            INTERVAL: {
              target: "fetching",
            },
          },
        },
      },
      onDone: {
        target: "form",
      },
    },
    pollingUpdateStatus: {
      initial: "updating",
      exit: ["reloadSegments"],
      states: {
        updating: {
          invoke: {
            src: "fetchUpdateStatus",
            onDone: [
              {
                actions: assign({ scenario: (context, event) => event.data }),
                cond: "updateFinished",
                target: "finished",
              },
              {
                target: "retry",
              },
            ],
            onError: [
              {
                actions: assign({
                  error: (context, event) => event.data,
                }),
                target: "failed",
              },
            ],
          },
        },
        finished: {
          type: "final",
        },
        failed: {
          after: {
            INTERVAL: {
              target: "updating",
            },
          },
        },
        retry: {
          after: {
            INTERVAL: {
              target: "updating",
            },
          },
        },
      },
      onDone: {
        target: "done",
      },
    },
    form: {
      initial: "ready",
      on: {
        CANCEL: {
          target: "done",
        },
        SUBMIT_FORM: {
          actions: assign({ formData: (context, event: SubmitFormEvent) => event.payload }),
          target: ".saving",
        },
      },
      states: {
        ready: {},
        failure: {},
        saving: {
          invoke: {
            src: "saveSegments",
            onDone: [
              {
                actions: "setBulkUpdateResponse",
                cond: "allValid",
                target: "#bulkEdit.pollingUpdateStatus",
              },
              {
                actions: "setBulkUpdateResponse",
                cond: "defaultValueExists",
                target: "#bulkEdit.validationSummary",
              },
              {
                actions: "setBulkUpdateResponse",
                target: "ready",
              },
            ],
            onError: [
              {
                target: "failure",
                actions: assign({
                  error: (context, event) => event.data.response.data,
                }),
              },
            ],
          },
        },
      },
    },
    validationSummary: {
      initial: "ready",
      on: {
        CANCEL: {
          target: "done",
        },
        PREVIOUS: {
          target: "form",
        },
        SUBMIT_FORM: {
          target: ".saving",
        },
      },
      states: {
        ready: {},
        failure: {},
        saving: {
          invoke: {
            src: "saveSegments",
            onDone: [
              {
                actions: "setBulkUpdateResponse",
                target: "#bulkEdit.pollingUpdateStatus",
              },
            ],
            onError: [
              {
                target: "failure",
                actions: assign({
                  error: (context, event) => event.data.response.data,
                }),
              },
            ],
          },
        },
      },
    },
    savingWithDefaultValues: {
      invoke: {
        src: "saveSegments",
        onDone: [
          {
            actions: assign({ bulkUpdateResponse: (context, event) => event.data }),
            target: "pollingUpdateStatus",
          },
        ],
      },
    },
    done: {
      entry: ["resetSelection", ""],
      exit: "exit",
      type: "final",
    },
  },
};

const xStateOptions: Partial<MachineOptions<Context, any>> = {
  guards: {
    updateFinished: (context: Context, event: { data: Scenario }) =>
      event.data.networkUpdateStatus !== NetworkUpdateStatus.UPDATING,
    allValid: (context: Context, event: { data: SegmentBulkUpdateResponse }) =>
      event.data.validationReportSummary.allValid,
    defaultValueExists: (context: Context, event: { data: SegmentBulkUpdateResponse }) =>
      event.data.validationReportSummary.defaultValueExists,
  },
  actions: {
    setBulkUpdateResponse: assign((context, event: { data: SegmentBulkUpdateResponse }) => {
      return { bulkUpdateResponse: event.data };
    }) as any,
  },
  delays: { INTERVAL: 1000, TIMEOUT: 10000 },
};

export const segmentBulkEditMachine = createMachine(xStateConfig, xStateOptions);
